import { useSeriesWatchtime } from '../../utils/useSeriesWatchtime';
import { useDefaultEpisode } from '../../utils/useDefaultEpisode';
import { useAppSelector } from '../../../modules/hooks';
import { selectEpisodeById } from '../../../modules/assets';
import { Button } from '../../Button/Button';
import { AssetButton } from '../../AssetButton/AssetButton';
import { Spinner } from '../../Spinner/Spinner';

import { useEpisodeData } from './useEpisodeData';

import type { Vendor } from '../../../types';
import type { SvpCategoryAdditional } from '@vgtv/api-client/lib/svp_category';
import type { SvpAssetAdditional } from '@vgtv/api-client/lib/svp_asset';
import type { FC, MouseEventHandler } from 'react';

import * as styles from './EpisodeButton.module.scss';

interface EpisodeButtonProps {
  category: SvpCategoryAdditional;
  vendor: Vendor;
  onClick?: (
    event: Parameters<MouseEventHandler>[0],
    asset: SvpAssetAdditional
  ) => void;
  onEpisodeButtonClick?: () => void;
  fallback?: JSX.Element;
}

export const EpisodeButton: FC<EpisodeButtonProps> = ({
  category,
  vendor,
  onClick,
  onEpisodeButtonClick,
  fallback = null,
}) => {
  const { isFetching, watchtime } = useSeriesWatchtime({
    categoryId: category.id,
    vendor,
  });
  const { data: defaultEpisode, isLoading: isDefaultEpisodeLoading } =
    useDefaultEpisode({
      category,
      vendor,
    });

  const { label } = useEpisodeData({ category, vendor });

  const asset =
    useAppSelector((state) =>
      selectEpisodeById(state, vendor, watchtime?.assetId)
    ) ?? defaultEpisode;

  const loadingButton = (
    <Button
      size="large"
      variant="contained"
      rounded
      disabled
      className={styles.episodeButton}
    >
      <Spinner size="small" />
    </Button>
  );

  if (isFetching || (!watchtime && isDefaultEpisodeLoading)) {
    return loadingButton;
  }

  if (asset) {
    return (
      <AssetButton
        asset={asset}
        label={label}
        onClick={onClick}
        onEpisodeButtonClick={onEpisodeButtonClick}
        size="large"
        variant="contained"
        rounded
        className={styles.episodeButton}
      />
    );
  }

  return fallback ?? loadingButton;
};
